import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { agentService } from '../../services/agentService';
import type { AgentDefinitionDto } from '../../types/agent';

interface AttachAgentDialogProps {
  open: boolean;
  onClose: () => void;
  onAttach: (childId: string, alias?: string, descriptionOverride?: string) => void;
  excludeIds?: string[];
}

export const AttachAgentDialog = ({ open, onClose, onAttach, excludeIds = [] }: AttachAgentDialogProps) => {
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AgentDefinitionDto | null>(null);
  const [alias, setAlias] = useState('');
  const [descOverride, setDescOverride] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    agentService
      .getAgents({ pageSize: 200, isActive: true })
      .then((res) => {
        setAgents(res.items.filter((a) => !excludeIds.includes(a.id)));
      })
      .finally(() => setLoading(false));
  }, [open, excludeIds.join(',')]);

  const handleSubmit = () => {
    if (!selected) return;
    onAttach(selected.id, alias || undefined, descOverride || undefined);
    setSelected(null);
    setAlias('');
    setDescOverride('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attach Sub-Agent</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Autocomplete
          options={agents}
          getOptionLabel={(o) => `${o.name} (${o.slug})`}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Agent"
              placeholder="Search agents..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mt: 1 }}
        />
        <TextField
          label="Alias (tool name override)"
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          placeholder={selected ? `call_agent_${selected.slug}` : ''}
          size="small"
        />
        <TextField
          label="Description override"
          value={descOverride}
          onChange={(e) => setDescOverride(e.target.value)}
          placeholder={selected?.description ?? ''}
          size="small"
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!selected}>
          Attach
        </Button>
      </DialogActions>
    </Dialog>
  );
};
